import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const cyan = "#00f5ff";
const amber = "#ffaa00";
const border = "rgba(0,245,255,0.15)";
const API = () => process.env.REACT_APP_API_URL;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const EMPTY_DIVISION_TAG = { jobId: "", divisionKey: "", split: false, splitMode: "pct", allocations: [] };

/** The expense body fields for a tag (for POST / PUT /api/expenses). */
export function divisionBody(tag, enabled) {
  if (!enabled) return tag.jobId ? { jobId: tag.jobId } : {};
  const out = { jobId: tag.jobId || null };
  if (tag.split) {
    out.splitMode = tag.splitMode;
    out.allocations = tag.allocations
      .filter((a) => a.divisionKey)
      .map((a) => (tag.splitMode === "amount" ? { divisionKey: a.divisionKey, amount: Number(a.value) || 0 } : { divisionKey: a.divisionKey, pct: Number(a.value) || 0 }));
  } else out.divisionKey = tag.divisionKey || "";
  return out;
}

/** "" when the split adds up, else the message to show. */
export function splitProblem(tag, total, t) {
  if (!tag.split) return "";
  const rows = tag.allocations.filter((a) => a.divisionKey);
  if (rows.length < 2) return t("divisions.split.needTwo");
  const sum = round2(rows.reduce((s, a) => s + (Number(a.value) || 0), 0));
  if (tag.splitMode === "amount") return Math.abs(sum - round2(total)) > 0.01 ? t("divisions.split.amountOff", { sum, total: round2(total) }) : "";
  return Math.abs(sum - 100) > 0.01 ? t("divisions.split.pctOff", { sum }) : "";
}

// Expense → workover job, division, or a split across divisions (Division
// P&L). Shown only to orgs with Rig services or Hydro-testing — for every
// other org /api/divisions answers enabled:false and this renders nothing.
export default function ExpenseDivisionPicker({ token, wellId, total, value, onChange, onEnabled, inputStyle, labelStyle, isMobile }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    axios
      .get(`${API()}/api/divisions`, { headers: { Authorization: "Bearer " + token } })
      .then((res) => {
        setInfo(res.data);
        if (onEnabled) onEnabled(!!res.data.enabled);
      })
      .catch(() => setInfo({ enabled: false }));
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    setJobs([]);
    if (!wellId || !info?.enabled) return;
    axios
      .get(`${API()}/api/jobs`, { headers: { Authorization: "Bearer " + token }, params: { wellId } })
      .then((res) => setJobs((Array.isArray(res.data) ? res.data : res.data.data || []).filter((j) => j.jobKind !== "own_well")))
      .catch(() => setJobs([]));
  }, [wellId, token, info]);

  if (!info || !info.enabled) return null;
  const divisions = info.divisions.filter((d) => d.active);
  const options = [...divisions.map((d) => ({ key: d.key, name: d.name })), { key: info.overheadKey, name: t("divisions.overhead") }];
  const set = (patch) => onChange({ ...value, ...patch });

  function pickJob(jobId) {
    const job = jobs.find((j) => j._id === jobId);
    // The job's division (else its rig's) becomes the default.
    const def = job ? job.divisionKey || job.rigId?.divisionKey || info.defaults?.workover || "" : value.divisionKey;
    set({ jobId, divisionKey: value.split ? value.divisionKey : def });
  }

  // Switching % ↔ $ converts the values so the split stays the same.
  function switchMode(mode) {
    if (!value.split) return startSplitWith(mode);
    if (mode === value.splitMode) return undefined;
    const conv = (v) => (mode === "amount" ? round2((total * Number(v)) / 100) : total > 0 ? round2((Number(v) / total) * 100) : 0);
    return set({ splitMode: mode, allocations: value.allocations.map((a) => ({ ...a, value: conv(a.value) })) });
  }

  const setRow = (i, patch) => set({ allocations: value.allocations.map((a, j) => (j === i ? { ...a, ...patch } : a)) });
  const problem = splitProblem(value, total, t);
  const chip = (on) => ({
    minHeight: "44px",
    padding: "0 14px",
    borderRadius: "22px",
    cursor: "pointer",
    fontSize: "13px",
    background: on ? "rgba(0,245,255,0.18)" : "none",
    border: `1px solid ${on ? cyan : "rgba(0,245,255,0.3)"}`,
    color: on ? cyan : "rgba(255,255,255,0.75)",
  });

  return (
    <div data-testid="expense-division" style={{ backgroundColor: "rgba(0,245,255,0.03)", border: `1px solid ${border}`, borderRadius: "10px", padding: "20px", marginBottom: "16px" }}>
      <div style={{ color: cyan, fontSize: "10px", fontWeight: "700", letterSpacing: "3px", marginBottom: "16px" }}>{t("divisions.expense.section")}</div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
        <div>
          <label style={labelStyle} htmlFor="expense-job">
            {t("divisions.expense.job")}
          </label>
          <select id="expense-job" value={value.jobId} onChange={(e) => pickJob(e.target.value)} style={{ ...inputStyle, minHeight: "44px" }}>
            <option value="">{t("divisions.expense.noJob")}</option>
            {jobs.map((j) => (
              <option key={j._id} value={j._id}>
                {[j.jobNumber, j.title].filter(Boolean).join(" — ")}
                {j.customerName ? ` · ${j.customerName}` : ""}
              </option>
            ))}
          </select>
        </div>
        {!value.split && (
          <div>
            <label style={labelStyle} htmlFor="expense-division">
              {t("divisions.expense.division")}
            </label>
            <select id="expense-division" value={value.divisionKey} onChange={(e) => set({ divisionKey: e.target.value })} style={{ ...inputStyle, minHeight: "44px" }}>
              <option value="">{t("divisions.expense.untagged")}</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
        <button type="button" style={chip(!value.split)} onClick={() => set({ split: false })}>
          {t("divisions.expense.oneDivision")}
        </button>
        <button type="button" style={chip(value.split && value.splitMode === "pct")} onClick={() => switchMode("pct")}>
          {t("divisions.expense.splitPct")}
        </button>
        <button type="button" style={chip(value.split && value.splitMode === "amount")} onClick={() => switchMode("amount")}>
          {t("divisions.expense.splitAmount")}
        </button>
      </div>
      {value.split && (
        <div style={{ marginTop: "12px" }}>
          {value.allocations.map((a, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: "8px", marginBottom: "8px", alignItems: "end" }}>
              <div>
                <label style={labelStyle} htmlFor={`split-div-${i}`}>
                  {t("divisions.expense.division")}
                </label>
                <select id={`split-div-${i}`} value={a.divisionKey} onChange={(e) => setRow(i, { divisionKey: e.target.value })} style={{ ...inputStyle, minHeight: "44px" }}>
                  <option value="">{t("divisions.expense.pick")}</option>
                  {options.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle} htmlFor={`split-val-${i}`}>
                  {value.splitMode === "amount" ? t("divisions.expense.amount") : t("divisions.expense.pct")}
                </label>
                <input id={`split-val-${i}`} inputMode="decimal" value={a.value} onChange={(e) => setRow(i, { value: e.target.value })} style={{ ...inputStyle, minHeight: "44px" }} />
              </div>
              <button
                type="button"
                disabled={value.allocations.length <= 2}
                onClick={() => set({ allocations: value.allocations.filter((_, j) => j !== i) })}
                style={{ minHeight: "44px", padding: "0 12px", background: "none", border: "1px solid rgba(255,68,102,0.5)", color: "#ff4466", borderRadius: "6px", cursor: "pointer" }}
              >
                {t("divisions.expense.remove")}
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => set({ allocations: [...value.allocations, { divisionKey: "", value: "" }] })}
            style={{ minHeight: "44px", padding: "0 14px", background: "none", border: "1px solid rgba(0,245,255,0.4)", color: cyan, borderRadius: "6px", cursor: "pointer" }}
          >
            {t("divisions.expense.addSplit")}
          </button>
          {problem && <div style={{ color: amber, fontSize: "13px", marginTop: "8px" }}>{problem}</div>}
        </div>
      )}
    </div>
  );

  function startSplitWith(mode) {
    const first = value.divisionKey && value.divisionKey !== info.overheadKey ? value.divisionKey : divisions[0]?.key || "";
    const second = divisions.find((d) => d.key !== first)?.key || info.overheadKey;
    const half = round2(total / 2);
    onChange({
      ...value,
      split: true,
      splitMode: mode,
      allocations: [
        { divisionKey: first, value: mode === "amount" ? half : 50 },
        { divisionKey: second, value: mode === "amount" ? round2(total - half) : 50 },
      ],
    });
  }
}
